import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CardComponent } from "@components/cards/card/card.component";
import { CardsComponent } from "@components/cards/cards.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { Observable, Subject, Subscription } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Anskaffningsformer, Arbetsordertyper, ConfirmDialogResponse, Produktionsstatus } from "~app/common/models/constants";
import { GrotvolymStatus } from "~app/common/models/planera/v2/HamtaKvalitetssakradGrotvolymResponseDTO";
import { IContent } from "~app/common/models/validationModel";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { FeatureService } from "~app/common/services/feature.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { MoveAtgarderToNewArbetsorderCommand } from "~app/features/planera/commands/MoveAtgarderToNewArbetsorderCommand";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { environment } from "~environments/environment";
import { ArbetsorderAtgardskedjaBehaviour } from "../behaviours/ArbetsorderAtgardskedjaBehaviour";
import { AttachAtgardToArbetsorderCommand } from "../commands/AttachAtgardToArbetsorderCommand";
import { MapEvents } from "../events/map-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { Produktionsstatusar } from "../services/arbetsorder.service";
import { AtgardPlaneringsstatusService } from "../services/atgard-planeringsstatus.service";
import { ArbetsorderAtgardService } from "./arbetsorder-atgard.service";
import { ArbetsorderAtgardProduktionAvslutadComponent } from "./produktion-avslutad/arbetsorder-atgard-produktion-avslutad.component";

@Component({
    selector: "rask-arbetsorder-atgard",
    templateUrl: "./arbetsorder-atgard.component.html",
    styleUrls: ["./arbetsorder-atgard.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardsComponent,
        CardComponent,
        NgClass,
        InputComponent,
        ButtonComponent,
        FormsModule,
        ReactiveFormsModule,
        ArbetsorderAtgardProduktionAvslutadComponent,
        TextmultilineComponent,
        MessagePanelComponent,
        NgbTooltip,
        AsyncPipe
    ]
})
export class ArbetsorderAtgardComponent implements OnInit, AfterViewInit, OnDestroy {
    arbetsorder: IArbetsorderDetail;
    canAvslutaArbetsorder = false;
    canFetchGrotvolymFromStina = false;
    detachedAtgarder: string[] = [];
    form: UntypedFormGroup;
    grotvolymMessage: IMessageModel[] = [];
    hovering: boolean;
    isFormDirty: boolean;
    slutrapportUrl$: Observable<string>;

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly atgardPlaneringsstatusService = inject(AtgardPlaneringsstatusService);
    private readonly atgardService = inject(ArbetsorderAtgardService);
    private readonly bridgeEvents: IBridgeEvent[] = [];
    private readonly busyService = inject(BusyService);
    private readonly featureService = inject(FeatureService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly raskModalService = inject(RaskModalService);

    private formSubscription: Subscription;
    private relateradeRvArbetsorderAreProduktionGodkand: boolean;

    get arbetsorderAvslutad(): string {
        return `${DateUtils.shortDateString(this.arbetsorder.tidpunkter.produktionGodkandDatum)} ${this.arbetsorder.produktionGodkandAv}`;
    }

    get arbetsorderHasUtbytesberakning(): boolean {
        if (!this.arbetsorder) {
            return false;
        }

        const justeringar = this.arbetsorder.utbytesberakningar.filter((u) =>
            !ObjectUtils.isNullOrUndefined(u.justeradKvantitet)
        );

        return justeringar.length > 0;
    }

    get canRemoveAtgard(): boolean {
        if (this.arbetsorder.isLeveransvirke || this.arbetsorder.isGrotskotning) {
            return false;
        }

        const antalAtgarder = this.arbetsorder.atgarder.filter((x) => !x.deleted).length;

        if (this.arbetsorder.skickadTillViol && antalAtgarder === 1) {
            return false;
        }

        return (
            this.arbetsorder.internProduktionsstatus === Produktionsstatusar.produktionEjPaborjad &&
            (
                this.arbetsorder.anskaffningsform === Anskaffningsformer.Virkeskop ||
                (
                    antalAtgarder > 1 &&
                    !this.atgardPlaneringsstatusService.isNotYetTillgangliga(
                        this.arbetsorder.atgarder
                    )
                )
            )

        );
    }

    get isAvslutaView(): boolean {
        if (!this.arbetsorder) {
            return false;
        }

        if (this.arbetsorder.isGrotskotning) {
            return (
                this.relateradeRvArbetsorderAreProduktionGodkand &&
                this.arbetsorder.tidpunkter.skotningAvslutadDatum &&
                this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionGodkand
            );
        }

        if (this.arbetsorder.isLeveransvirke) {
            return this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionGodkand;
        }

        return (
            this.arbetsorder.tidpunkter.skotningAvslutadDatum &&
            this.arbetsorder.produktionsstatus !== Produktionsstatus.ProduktionGodkand
        );
    }

    get isGrot(): boolean {
        return this.arbetsorder.isGrotskotning;
    }

    get isLeveransvirke(): boolean {
        return this.arbetsorder.isLeveransvirke;
    }

    get sumAtgard(): number {
        if (!this.arbetsorder) {
            return 0;
        }

        return this.arbetsorder.atgarder.reduce(
            (sum, current) => sum + current.volymAtgard,
            0
        );
    }

    avslutaArbetsorder(): void {
        this.raskModalService.confirm(new ModalSettings("Avsluta arbetsorder", "Vill du verkligen avsluta arbetsorder? Det går inte att ångra"))
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const worker: Observable<void> = this.busyService.addWork(
                        this.atgardService.avslutaArbetsorder(this.arbetsorder.id)
                    );
                    worker.subscribe((result) => {
                        this.arbetsorderMediatorService.reload();
                    });
                }
            });
    }

    fetchGrotvolymFromStina(): void {
        const worker = this.busyService.addWork(
            this.atgardService.fetchGrotvolymFromStina(this.arbetsorder.id)
        );

        worker.subscribe((result) => {
            switch (result.status) {
                case GrotvolymStatus.serverError:
                    this.raskModalService.error("Fel", { message: "Kontakt med Stina kan inte upprättas" } as IContent).subscribe();
                    break;
                case GrotvolymStatus.arbetsorderSaknas:
                case GrotvolymStatus.volymSaknas:
                    this.raskModalService.info(new ModalSettings("Info", "Maskinlaget har inte kvitterat grotvolymer i Stina, kontakta maskinlaget"))
                        .subscribe();
                    break;
                case GrotvolymStatus.volymSaknasDelvis:
                    this.raskModalService.info(new ModalSettings("Info", "Maskinlaget har inte kvitterat grotvolymer för alla åtgärder i Stina, kontakta maskinlaget"))
                        .subscribe();
                    break;
                default:
                    break;
            }
        });
    }

    handleAtgardMoveCommands(atgard: IAtgard): void {
        if (!this.detachedAtgarder.some((x) => x === atgard.guid)) {
            return;
        }

        const movedAtgard = this.detachedAtgarder.indexOf(atgard.guid, 1);
        this.detachedAtgarder.splice(movedAtgard, 1);
        if (this.detachedAtgarder.length > 0) {
            this.arbetsorder.commandManager.addOrUpdate(
                new MoveAtgarderToNewArbetsorderCommand(
                    this.arbetsorder.id,
                    this.detachedAtgarder
                ),
                (c) => c.$type === MoveAtgarderToNewArbetsorderCommand.$type
            );
        } else {
            this.arbetsorder.commandManager.remove(
                new MoveAtgarderToNewArbetsorderCommand(
                    this.arbetsorder.id,
                    this.detachedAtgarder
                )
            );
        }
    }

    handleAtgardAdded = (payload: any): void => {
        if (!this.arbetsorder) {
            return;
        }

        this.handleAtgardMoveCommands(payload.atgard);
        this.arbetsorder.atgarder.push(payload.atgard);
        this.arbetsorder.commandManager.addOrUpdate(
            new AttachAtgardToArbetsorderCommand(
                this.arbetsorder.id,
                payload.atgard.guid
            ),
            (c) =>
                c.$type === AttachAtgardToArbetsorderCommand.$type &&
                c.atgardGuid === payload.atgard.guid
        );
    };

    hoverToggle(atgard: IAtgard, hovering: boolean): void {
        this.atgardService.hoverToggle(atgard, hovering);
    }

    mapHover(event): void {
        const atgard = this.arbetsorder?.atgarder?.find(a => a.guid === event.guid);
        if (atgard) {
            atgard.isHovering = event.isMouseover;
        }
    }

    ngAfterViewInit(): void {
        if (this.isAvslutaView) {
            this.setCanAvslutaArbetsorder();
        }
    }

    ngOnDestroy(): void {
        this.planeraKartaEventService.unsubscribeMany(this.bridgeEvents);
        this.bridgeEvents.splice(0);
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.slutrapportUrl$ = this.featureService.getFeatureActive("StinaSlutrapport").pipe(
            map(active => {
                if (active) {
                    return `${environment.slutrapportUrl}/${this.arbetsorder.beteckning}`;
                }
                return `${environment.prologGuiBaseUrl}Web/dokument/dsr/${this.arbetsorder.beteckning}`;
            })
        );

        this.setupForm();

        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                if (changeEvent) {
                    this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                    this.initArbetsorder();
                }
            });

        this.arbetsorderMediatorService.dirty
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(dirty => {
                if (dirty != null) {
                    this.isFormDirty = dirty;
                    this.setCanAvslutaArbetsorder();
                }
            });

        this.bridgeEvents.push(this.planeraKartaEventService.subscribe(
            MapEvents.mouseOverAtgard,
            (event) => this.mapHover(event)
        ));

        this.bridgeEvents.push(this.planeraKartaEventService.subscribe(
            MapEvents.addAtgardToArbetsorder,
            this.handleAtgardAdded
        ));
    }

    removeAtgard(atgard: IAtgard): void {
        this.detachedAtgarder.push(atgard.guid);
        this.arbetsorder.atgarder = this.arbetsorder.atgarder.filter(
            (x) => x.id !== atgard.id
        );
        this.arbetsorder.commandManager.addOrUpdate(
            new MoveAtgarderToNewArbetsorderCommand(
                this.arbetsorder.id,
                this.detachedAtgarder
            ),
            (c) => c.$type === MoveAtgarderToNewArbetsorderCommand.$type
        );
    }

    private atgarderValidForAvsluta(): boolean {
        if (this.arbetsorder.isLeveransvirke) {
            return true;
        }

        if (!this.arbetsorder.atgarder.some(x => !!x.atgardUtford)) {
            // Ingen åtgärd är markerad som utförd
            return false;
        }

        return this.arbetsorder.atgarder.every(x => !ObjectUtils.isNullOrUndefined(x.atgardUtford)); // Aktivt val (true/false)
    }

    private getGrotvolymSaknasMessage(): IMessageModel[] {
        if (!this.arbetsorder?.atgarder?.length
            || !this.kvalitetssakradGrotvolymSaknas(this.arbetsorder)
            || !this.canFetchGrotvolymFromStina
        ) {
            return [];
        }

        return [
            {
                message: "Tryck på knappen 'Hämta grotvolym' för att uppdatera volymen innan arbetsordern avslutas",
                severity: MessageSeverity.Warning,
                anchor: null
            }
        ];
    }

    private initArbetsorder(): void {
        const atgardskedjeKopplingar = new ArbetsorderAtgardskedjaBehaviour(this.arbetsorder).getRelateradeAtgardskopplingar();
        if (ObjectUtils.isNullOrUndefined(atgardskedjeKopplingar)) {
            this.relateradeRvArbetsorderAreProduktionGodkand = true;
        } else {
            this.relateradeRvArbetsorderAreProduktionGodkand = atgardskedjeKopplingar.every((x) =>
                x.arbetsorderTyp !== Arbetsordertyper.Grotskotning &&
                x.produktionsstatus === Produktionsstatus.ProduktionGodkand
            );
        }

        this.detachedAtgarder?.splice(0);

        this.canFetchGrotvolymFromStina = this.isAvslutaView && this.arbetsorder.atgarder.some(x => x.grotuttagProduktionsplanerat);
        this.grotvolymMessage = this.getGrotvolymSaknasMessage();
    }

    private kvalitetssakradGrotvolymSaknas(arbetsorder: IArbetsorderDetail) {
        return arbetsorder.atgarder.some(
            x => (x.grotAnpassad && x.kvalitetssakradGrotvolym === null)
                || (x.grotuttagProduktionsplanerat && x.kvalitetssakradGrotvolym === null && !ObjectUtils.hasValue(x.grotAnpassad))
        );
    }

    private setCanAvslutaArbetsorder(): void {
        if (!this.arbetsorder) {
            return;
        }

        if (this.form?.valid && !this.isFormDirty && this.atgarderValidForAvsluta()) {
            this.canAvslutaArbetsorder = true;
        } else {
            this.canAvslutaArbetsorder = false;
        }
    }

    private readonly onFormChanges = (): void => {
        this.setCanAvslutaArbetsorder();
    };

    private setupForm(): void {
        this.formSubscription?.unsubscribe();
        this.form = new UntypedFormGroup({});
        this.form.markAsPristine();
        this.formSubscription = this.form.valueChanges.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(this.onFormChanges);
    }
}
